import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, Star, Heart, Calendar, BookMarked, BookOpen, Sparkles, Filter, Bell, LogOut, User, Plus, Shield, FileText, Clock, Eye, Newspaper, ArrowRight, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '@/components/Footer';
import AmazonBooks from '@/components/AmazonBooks';
import ShareButtons from '@/components/ShareButtons';
import { AreaFilterAutocomplete } from '@/components/AreaSelector';
import NotificationBadge from '@/components/NotificationBadge';

const QUALIS_OPTIONS = ['A1', 'A2', 'A3', 'A4', 'B1', 'B2', 'B3', 'B4', 'B5', 'C'];
const KNOWLEDGE_AREAS = [
  'Ciências Exatas e da Terra',
  'Ciências Biológicas',
  'Engenharias',
  'Ciências da Saúde',
  'Ciências Agrárias',
  'Ciências Sociais Aplicadas',
  'Ciências Humanas',
  'Linguística, Letras e Artes',
  'Multidisciplinar'
];

const DEADLINE_OPTIONS = [
  { value: '', label: 'Todos os prazos' },
  { value: '15', label: 'Até 15 dias' },
  { value: '30', label: 'Até 30 dias' },
  { value: '60', label: 'Até 60 dias' },
  { value: '90', label: 'Até 90 dias' },
  { value: 'continuous', label: 'Fluxo contínuo' }
];

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [totalCalls, setTotalCalls] = useState(0);  // Total from backend
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAiSearch, setShowAiSearch] = useState(false);
  const [aiAbstract, setAiAbstract] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSearchUsed, setAiSearchUsed] = useState(false);
  const [aiCredits, setAiCredits] = useState(1);
  const [journals, setJournals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('relevant'); // Default: mais relevantes
  const ITEMS_PER_PAGE = 10;
  const [filters, setFilters] = useState({
    search: '',
    qualis: '',
    area: '',
    grandeArea: '',
    subarea: '',
    deadline_filter: '',
    journal: ''
  });

  const SORT_OPTIONS = [
    { value: 'relevant', label: 'Mais relevantes primeiro' },
    { value: 'newest', label: 'Mais recentes primeiro' },
    { value: 'oldest', label: 'Mais antigas primeiro' },
    { value: 'deadline_asc', label: 'Menor prazo primeiro' },
    { value: 'deadline_desc', label: 'Maior prazo primeiro' }
  ];

  useEffect(() => {
    fetchCalls();
    fetchFavorites();
    fetchJournals();
    checkAiSearchStatus();
    
    // Send heartbeat to track online users
    const sendHeartbeat = () => {
      api.post('/api/heartbeat').catch(() => {});
    };
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 60000); // Every minute
    
    return () => clearInterval(heartbeatInterval);
  }, []);

  const fetchJournals = async () => {
    try {
      const response = await api.get('/api/journals');
      setJournals(response.data.journals || []);
    } catch (error) {
      console.error('Erro ao buscar revistas');
    }
  };

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.qualis) params.append('qualis', filters.qualis);
      // Send area filter (from autocomplete)
      if (filters.area) params.append('area', filters.area);
      if (filters.grandeArea) params.append('grande_area', filters.grandeArea);
      if (filters.subarea) params.append('subarea', filters.subarea);
      if (filters.deadline_filter) params.append('deadline_filter', filters.deadline_filter);
      if (filters.journal) params.append('journal', filters.journal);
      params.append('limit', '500');  // Get all calls

      const response = await api.get(`/api/calls?${params.toString()}`);
      setCalls(response.data.calls);
      setTotalCalls(response.data.total || response.data.calls.length);
      setCurrentPage(1); // Reset to first page when fetching new data
    } catch (error) {
      toast.error('Erro ao buscar chamadas');
    } finally {
      setLoading(false);
    }
  };

  // Sort function
  const sortCalls = (callsToSort) => {
    const sorted = [...callsToSort];
    
    switch (sortOrder) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'deadline_asc':
        return sorted.sort((a, b) => {
          // Continuous flow calls go to the end
          if (a.continuous_flow && !b.continuous_flow) return 1;
          if (!a.continuous_flow && b.continuous_flow) return -1;
          if (a.continuous_flow && b.continuous_flow) return 0;
          return new Date(a.deadline) - new Date(b.deadline);
        });
      case 'deadline_desc':
        return sorted.sort((a, b) => {
          // Continuous flow calls go first
          if (a.continuous_flow && !b.continuous_flow) return -1;
          if (!a.continuous_flow && b.continuous_flow) return 1;
          if (a.continuous_flow && b.continuous_flow) return 0;
          return new Date(b.deadline) - new Date(a.deadline);
        });
      case 'relevant':
      default:
        // Relevance: continuous flow first, then by deadline (nearest first), then by views
        return sorted.sort((a, b) => {
          // Continuous flow first
          if (a.continuous_flow && !b.continuous_flow) return -1;
          if (!a.continuous_flow && b.continuous_flow) return 1;
          
          // Then by deadline (nearest first)
          const deadlineA = a.deadline ? new Date(a.deadline) : new Date('9999-12-31');
          const deadlineB = b.deadline ? new Date(b.deadline) : new Date('9999-12-31');
          if (deadlineA - deadlineB !== 0) return deadlineA - deadlineB;
          
          // Then by views (most viewed first)
          return (b.views || 0) - (a.views || 0);
        });
    }
  };

  // Pagination logic with sorting
  const sortedCalls = sortCalls(calls);
  const totalPages = Math.ceil(sortedCalls.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentCalls = sortedCalls.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    setCurrentPage(1); // Reset to first page when changing sort
  };

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/api/favorites');
      setFavorites(response.data.favorites.map(f => f.call_id));
    } catch (error) {
      console.error('Failed to fetch favorites');
    }
  };

  const createReminder = async (callId) => {
    try {
      await api.post(`/api/reminders?call_id=${callId}`);
      toast.success('Lembrete criado! Você receberá emails 7 dias e 1 dia antes do prazo.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar lembrete');
    }
  };

  const handleAiSearch = async () => {
    if (!aiAbstract.trim()) {
      toast.error('Por favor, insira o resumo do seu artigo');
      return;
    }

    setAiLoading(true);
    try {
      const response = await api.post('/api/ai/semantic-search', { abstract: aiAbstract, limit: 10 });
      setCalls(response.data.matches);
      const remaining = response.data.credits_remaining;
      setAiCredits(remaining);
      if (remaining === 0) {
        setAiSearchUsed(true);
      }
      toast.success(`Busca com IA concluída! Você tem ${remaining} busca(s) restante(s).`);
      setShowAiSearch(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Você não tem créditos de IA. Doe R$ 3,99 para ganhar 5 buscas!');
        setAiSearchUsed(true);
        setAiCredits(0);
      } else {
        toast.error('Erro na busca: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleKeywordSearch = async () => {
    if (!aiAbstract.trim()) {
      toast.error('Por favor, insira o resumo do seu artigo');
      return;
    }

    setAiLoading(true);
    try {
      const response = await api.post('/api/ai/keyword-search', { abstract: aiAbstract, limit: 10 });
      setCalls(response.data.matches);
      const keywords = response.data.keywords || [];
      if (keywords.length > 0) {
        toast.success(`Encontradas ${response.data.matches.length} chamadas! Palavras-chave: ${keywords.slice(0, 5).join(', ')}`);
      } else {
        toast.info('Nenhuma chamada encontrada. Tente reformular seu resumo.');
      }
      setShowAiSearch(false);
    } catch (error) {
      toast.error('Erro na busca: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiLoading(false);
    }
  };

  const checkAiSearchStatus = async () => {
    try {
      const response = await api.get('/api/ai/search-status');
      setAiCredits(response.data.credits || 0);
      setAiSearchUsed(!response.data.can_use_ai);
    } catch {
      setAiSearchUsed(true);
      setAiCredits(0);
    }
  };

  const toggleFavorite = async (callId) => {
    try {
      if (favorites.includes(callId)) {
        await api.delete(`/api/favorites/${callId}`);
        setFavorites(favorites.filter(id => id !== callId));
        toast.success('Removido dos favoritos');
      } else {
        await api.post(`/api/favorites?call_id=${callId}`);
        setFavorites([...favorites, callId]);
        toast.success('Adicionado aos favoritos');
      }
    } catch (error) {
      toast.error('Erro ao atualizar favoritos');
    }
  };

  const getQualisClass = (qualis) => {
    return `qualis-${qualis.toLowerCase()}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchCalls();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      qualis: '',
      area: '',
      grandeArea: '',
      subarea: '',
      deadline_filter: '',
      journal: ''
    });
    setTimeout(() => fetchCalls(), 100);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#002FA7] flex items-center justify-center">
                <BookMarked className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-[#002FA7]">Quero Publicar</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <Button
                data-testid="suggest-call-btn"
                variant="outline"
                size="sm"
                onClick={() => navigate('/suggest-call')}
              >
                <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Sugerir Chamada Para Publicação</span>
                <span className="sm:hidden">Sugerir</span>
              </Button>
              {user?.role === 'admin' && (
                <Button
                  data-testid="admin-panel-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                >
                  <Shield className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Painel Admin</span>
                  <span className="sm:hidden">Admin</span>
                </Button>
              )}
              <Button
                data-testid="my-suggestions-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/my-suggestions')}
              >
                <FileText className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Minhas Sugestões</span>
              </Button>
              <NotificationBadge />
              <Button
                data-testid="profile-btn"
                variant="ghost"
                size="icon"
                onClick={() => navigate('/profile')}
              >
                <User className="h-5 w-5" />
              </Button>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={logout}
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* AI Search Banner */}
      <div className="ai-search-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          {!showAiSearch ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-6 w-6 text-[#002FA7]" />
                <div>
                  <h3 className="font-bold text-base sm:text-lg">Busca Semântica com IA</h3>
                  <p className="text-xs sm:text-sm text-[#4B4C52]">Cole o resumo do seu artigo e encontre chamadas compatíveis</p>
                </div>
              </div>
              <Button
                data-testid="open-ai-search-btn"
                onClick={() => setShowAiSearch(true)}
                className="bg-[#002FA7] hover:bg-[#001f75] w-full sm:w-auto"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Buscar com IA
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Cole o resumo do seu artigo científico</Label>
                <Textarea
                  data-testid="ai-abstract-input"
                  value={aiAbstract}
                  onChange={(e) => setAiAbstract(e.target.value)}
                  placeholder="Ex: Este estudo investiga os efeitos da mudança climática na biodiversidade marinha..."
                  rows={6}
                  className="mt-2"
                />
              </div>
              
              {/* Status da busca com IA */}
              {aiCredits === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                  <strong>Você não tem créditos de busca com IA.</strong>{' '}
                  <button 
                    onClick={() => navigate('/donate')} 
                    className="underline font-semibold hover:text-amber-900"
                  >
                    Doe R$ 3,99 e ganhe 5 buscas!
                  </button>
                </div>
              )}
              {aiCredits > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                  <Sparkles className="h-4 w-4 inline mr-1" />
                  Você tem <strong>{aiCredits} busca(s) com IA</strong> disponível(is).
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Busca por palavras-chave - sempre disponível */}
                <Button
                  data-testid="run-keyword-search-btn"
                  onClick={handleKeywordSearch}
                  disabled={aiLoading}
                  variant="outline"
                  className="border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/10"
                >
                  {aiLoading ? 'Processando...' : 'Buscar por Palavras-chave'}
                </Button>
                
                {/* Busca com IA - só se tiver créditos */}
                {aiCredits > 0 && (
                  <Button
                    data-testid="run-ai-search-btn"
                    onClick={handleAiSearch}
                    disabled={aiLoading}
                    className="bg-[#002FA7] hover:bg-[#001f75]"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {aiLoading ? 'Processando...' : `Buscar com IA (${aiCredits} restante${aiCredits > 1 ? 's' : ''})`}
                  </Button>
                )}
                
                {/* Link para comprar créditos */}
                {aiCredits === 0 && (
                  <Button
                    onClick={() => navigate('/donate')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Comprar Buscas com IA
                  </Button>
                )}
                
                <Button
                  data-testid="cancel-ai-search-btn"
                  variant="ghost"
                  onClick={() => setShowAiSearch(false)}
                >
                  Cancelar
                </Button>
              </div>
              
              <p className="text-xs text-gray-500">
                {aiCredits > 0 
                  ? `Busca por palavras-chave é ilimitada. Busca com IA usa inteligência artificial (${aiCredits} busca${aiCredits > 1 ? 's' : ''} disponível${aiCredits > 1 ? 'is' : ''}).`
                  : 'A busca por palavras-chave é gratuita e ilimitada. Doe R$ 3,99 para ganhar 5 buscas com IA!'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Blog & Instagram CTA Banners */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Blog Banner */}
          <div 
            onClick={() => navigate('/blog')}
            className="bg-gradient-to-r from-[#002FA7] to-[#0050FF] rounded-xl p-4 sm:p-5 cursor-pointer hover:shadow-lg transition-all group"
            data-testid="blog-cta-banner"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg flex-shrink-0">
                <Newspaper className="h-6 w-6 text-white" />
              </div>
              <div className="text-white flex-1 min-w-0">
                <h3 className="font-bold text-lg">Blog: Dicas para Publicar</h3>
                <p className="text-sm text-white/80 line-clamp-1">Artigos sobre como publicar em revistas de alto impacto</p>
              </div>
              <ArrowRight className="h-5 w-5 text-white/70 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </div>
          </div>

          {/* Instagram Banner */}
          <a 
            href="https://instagram.com/quero_publicar"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-[#E1306C] via-[#C13584] to-[#833AB4] rounded-xl p-4 sm:p-5 cursor-pointer hover:shadow-lg transition-all group"
            data-testid="instagram-cta-banner"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg flex-shrink-0">
                <Instagram className="h-6 w-6 text-white" />
              </div>
              <div className="text-white flex-1 min-w-0">
                <h3 className="font-bold text-lg">Siga-nos no Instagram</h3>
                <p className="text-sm text-white/80 line-clamp-1">@quero_publicar • Novidades e dicas diárias</p>
              </div>
              <ArrowRight className="h-5 w-5 text-white/70 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </div>
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
        {/* Horizontal Filters */}
        <div className="bg-white border border-[#E4E4E7] rounded-md p-4 sm:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5" />
            <h2 className="font-bold text-lg">Filtros</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
            {/* Search */}
            <div>
              <Label className="text-sm font-semibold">Busca</Label>
              <Input
                data-testid="search-input"
                placeholder="Título, descrição..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Revista */}
            <div>
              <Label className="text-sm font-semibold">Revista</Label>
              <select
                data-testid="journal-filter"
                value={filters.journal}
                onChange={(e) => handleFilterChange('journal', e.target.value)}
                className="mt-1 w-full border border-[#E4E4E7] rounded-md p-2 text-sm h-10"
              >
                <option value="">Todas as revistas</option>
                {journals.map(journal => (
                  <option key={journal} value={journal}>{journal}</option>
                ))}
              </select>
            </div>

            {/* Qualis */}
            <div>
              <Label className="text-sm font-semibold">QUALIS</Label>
              <select
                data-testid="qualis-filter"
                value={filters.qualis}
                onChange={(e) => handleFilterChange('qualis', e.target.value)}
                className="mt-1 w-full border border-[#E4E4E7] rounded-md p-2 text-sm h-10"
              >
                <option value="">Todos</option>
                {QUALIS_OPTIONS.map(q => (
                  <option key={q} value={q}>{q}</option>
                ))}
              </select>
            </div>

            {/* Área - Autocomplete */}
            <div className="lg:col-span-2">
              <AreaFilterAutocomplete
                value={filters.area}
                onChange={(area, grandeArea, subarea) => {
                  setFilters(prev => ({ 
                    ...prev, 
                    area,
                    grandeArea: grandeArea || '',
                    subarea: subarea || ''
                  }));
                }}
                label="Área"
                placeholder="Digite: sociologia, direito, educação..."
              />
            </div>

            {/* Prazo */}
            <div>
              <Label className="text-sm font-semibold">Prazo</Label>
              <select
                data-testid="deadline-filter"
                value={filters.deadline_filter}
                onChange={(e) => handleFilterChange('deadline_filter', e.target.value)}
                className="mt-1 w-full border border-[#E4E4E7] rounded-md p-2 text-sm h-10"
              >
                {DEADLINE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="apply-filters-btn"
              onClick={applyFilters}
              className="bg-[#002FA7] hover:bg-[#001f75]"
            >
              Aplicar Filtros
            </Button>
            <Button
              data-testid="clear-filters-btn"
              variant="outline"
              onClick={clearFilters}
            >
              Limpar Filtros
            </Button>
          </div>

        </div>

        {/* Results Header */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
              {totalCalls} Chamadas Encontradas
            </h2>
            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium text-[#4B4C52] whitespace-nowrap">Ordenar por:</Label>
              <select
                data-testid="sort-select"
                value={sortOrder}
                onChange={(e) => handleSortChange(e.target.value)}
                className="border border-[#E4E4E7] rounded-md px-3 py-1.5 text-sm bg-white hover:border-[#002FA7] focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none transition-colors"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <Button
            data-testid="view-favorites-btn"
            variant="outline"
            onClick={() => navigate('/favorites')}
          >
            <Star className="h-4 w-4 mr-2" />
            Ver Favoritos
          </Button>
        </div>

        {/* Calls List */}
        {loading ? (
          <div className="text-center py-12">Carregando...</div>
        ) : calls.length === 0 ? (
          <div className="bg-white border border-[#E4E4E7] rounded-md p-8 sm:p-12 text-center">
            <BookOpen className="h-16 w-16 mx-auto text-[#4B4C52] mb-4" />
            <p className="text-[#4B4C52]">Nenhuma chamada encontrada</p>
          </div>
        ) : (
          <div className="space-y-4">
            {currentCalls.map(call => (
              <div
                key={call.call_id}
                data-testid={`call-item-${call.call_id}`}
                className="bg-white border border-[#E4E4E7] rounded-md p-4 sm:p-6 hover:border-[#002FA7] transition-colors"
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold tracking-tight mb-2 break-words">{call.title}</h3>
                    <p className="text-sm text-[#4B4C52] mb-3">{call.journal_name}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <ShareButtons call={call} compact={true} />
                    <Button
                      data-testid={`favorite-btn-${call.call_id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleFavorite(call.call_id)}
                    >
                      <Heart
                        className={`h-5 w-5 ${favorites.includes(call.call_id) ? 'fill-red-500 text-red-500' : ''}`}
                      />
                    </Button>
                  </div>
                </div>

                <p className="text-sm mb-4 line-clamp-2">{call.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className={`${getQualisClass(call.qualis_classification)} font-semibold`}>
                    QUALIS {call.qualis_classification}
                  </Badge>
                  {call.continuous_flow && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      <Clock className="h-3 w-3 mr-1" />
                      Fluxo Contínuo
                    </Badge>
                  )}
                  {call.is_free && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Gratuito
                    </Badge>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-[#4B4C52] mb-4">
                  {!call.continuous_flow && call.deadline && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Prazo: {formatDate(call.deadline)}</span>
                    </div>
                  )}
                  {call.knowledge_areas && call.knowledge_areas.length > 0 && (
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      <span className="truncate max-w-[200px]">{call.knowledge_areas[0]}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {!call.continuous_flow && call.deadline && (
                    <Button
                      data-testid={`reminder-btn-${call.call_id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => createReminder(call.call_id)}
                    >
                      <Bell className="h-3 w-3 mr-1" />
                      Criar Lembrete
                    </Button>
                  )}
                  <Button
                    data-testid={`view-call-btn-${call.call_id}`}
                    size="sm"
                    className="bg-[#002FA7] hover:bg-[#001f75]"
                    onClick={() => navigate(`/chamada/${call.slug || call.call_id}`)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Ver chamada completa
                  </Button>
                </div>

                {/* Amazon Books - Leituras Recomendadas */}
                <AmazonBooks 
                  keywords={[
                    ...(call.knowledge_areas || []),
                    call.title?.split(' ').slice(0, 3).join(' ') || ''
                  ].filter(Boolean)}
                />
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 pt-6 border-t border-[#E4E4E7]">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    className="hidden sm:flex"
                  >
                    Primeira
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      // Show first, last, current, and adjacent pages
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(page)}
                            className={currentPage === page ? "bg-[#002FA7]" : ""}
                          >
                            {page}
                          </Button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return <span key={page} className="px-1">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="hidden sm:flex"
                  >
                    Última
                  </Button>
                </div>
                <p className="text-sm text-[#4B4C52]">
                  Página {currentPage} de {totalPages} ({totalCalls} chamadas)
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DashboardPage;
